/**
 * G6. Ребёнок: «Ночь». При `child.born` (и записи решения `family.child.have`)
 * и раз в год, пока младшему ≤ 3 лет (`flags.youngestAge`, ведёт тик):
 * 10-секундная игра «укачать» — тап в ритм плача, четыре такта, окно ±300 мс.
 * На ребёнка точность не влияет; на здоровье игрока — да: −2 при промахах
 * (недосып), 0 при попадании во все такты. «Пропустить» — недосып (−2):
 * это шутка с правдой внутри.
 *
 * Триггеры по событию и по возрасту совпадают в год рождения — хост
 * предлагает игру один раз в год по id.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome } from '@life/engine';
import type { MinigameDef } from '../types';
import { beatHits, nothing, youngestAgeOf } from './common';

export const NIGHT_MAX_CHILD_AGE = 3;
export const NIGHT_BEATS = 4;
export const NIGHT_FIRST_BEAT_MS = 2000;
export const NIGHT_BEAT_MS = 2000;
export const NIGHT_WINDOW_MS = 300;
export const NIGHT_DURATION_MS = 10_000;
export const NIGHT_MISS_HEALTH = -2;
/** Возраст игрока, в котором возможны маленькие дети (триггер по возрасту; доступность решает `youngestAge`). */
export const NIGHT_MIN_AGE = 14;
export const NIGHT_MAX_AGE = 80;
export const NIGHT_BORN_EVENTS = ['child.born', 'decision.family.child.have'] as const;

export interface NightState {
  /** Моменты плача от старта, мс. */
  beats: number[];
  /** Окно попадания ±мс. */
  window: number;
  durationMs: number;
}

export type NightInput = { taps: number[] };

function result(misses: number): MinigameOutcome {
  const m = Math.max(0, misses);
  if (m === 0) return { ...nothing(), stats: { health: 0 }, logKey: 'minigame.night.slept', logParams: { misses: 0 } };
  return { ...nothing(), stats: { health: NIGHT_MISS_HEALTH }, logKey: 'minigame.night.tired', logParams: { misses: m } };
}

export const night: MinigameDef<NightInput, NightState> = {
  id: 'night',
  section: 'G',
  triggers: [
    ...NIGHT_BORN_EVENTS.map((event) => ({ kind: 'event' as const, event })),
    { kind: 'age', min: NIGHT_MIN_AGE, max: NIGHT_MAX_AGE, everyYears: 1 },
  ],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    const youngest = youngestAgeOf(life);
    return life.children > 0 && youngest !== null && youngest <= NIGHT_MAX_CHILD_AGE;
  },
  setup(): NightState {
    const beats: number[] = [];
    for (let i = 0; i < NIGHT_BEATS; i += 1) beats.push(NIGHT_FIRST_BEAT_MS + i * NIGHT_BEAT_MS);
    return { beats, window: NIGHT_WINDOW_MS, durationMs: NIGHT_DURATION_MS };
  },
  play(state: NightState, input: NightInput, _life: Life): MinigameOutcome {
    return result(state.beats.length - beatHits(state.beats, state.window, input.taps));
  },
  expected(state: NightState): MinigameOutcome {
    return result(state.beats.length);
  },
};
